import React, { useContext, useEffect, useRef, useState } from 'react'

import styles from './styles.module.scss'
import classNames from 'classnames'
import { getLogger } from '@deltachat-desktop/shared/logger'
import { ForceMutedAudioPlayer } from './ForceMutedAudioPlayer'
import {
  MediaPlayerMutexContext,
  type MediaPlayerMutexContextValue,
} from '../../contexts/MediaPlayerMutexContext'
import { formatAudioTime, nextPlaybackRate } from './utils'
import { WaveformCanvas } from './Waveform'
import { getPeaks } from './peaks'
import { positionStore } from './positionStore'
import { IconButton } from '../Icon'
import useTranslationFunction from '../../hooks/useTranslationFunction'
import { useSettingsStore } from '../../stores/settings'

const log = getLogger('AudioPlayer')

type Props = React.AudioHTMLAttributes<HTMLAudioElement> & {
  src: string
}

// Apply a remembered position to a cold local element (before play / on
// metadata). Guarded so a fresh message (t null) or a near-complete one
// (t >= duration - 1) starts at 0.
function restorePosition(el: HTMLAudioElement, src: string): void {
  const t = positionStore.getPosition(src)
  if (t != null && t > 1 && t < el.duration - 1) {
    el.currentTime = t
  }
}

/**
 * Mutex-aware audio player. When one player starts playing, others get paused.
 */
export function AudioPlayer({
  onPlayNonProgrammatic,
  ...props
}: Omit<Props, keyof MediaPlayerMutexContextValue['eventListeners']> &
  Pick<Props, 'onPlay' | 'onPause'> & {
    /**
     * `onPlay` that is caused by the user (not programmatic).
     * The regular `play` event might fire as a consequence
     * of us calling `play()`.
     */
    onPlayNonProgrammatic?: React.ReactEventHandler<HTMLMediaElement>
  }) {
  const ref = useRef<HTMLAudioElement>(null)

  const mediaPlayerMutexCtx = useContext(MediaPlayerMutexContext)

  const ignoreOnePlayEvent = useRef(false)
  const ignoreOnePauseEvent = useRef(false)

  const customControlsEnabled =
    useSettingsStore()[0]?.desktopSettings.experimentalAudioPlayerControls ===
    true
  // Read the setting from a ref inside the mirror effect's cleanup WITHOUT
  // adding it to the effect deps — a settings toggle must not re-run this
  // effect (its cleanup would pause+zero the local element mid-play, the
  // desync the render-tree comment below guards against).
  const customControlsEnabledRef = useRef(customControlsEnabled)
  customControlsEnabledRef.current = customControlsEnabled

  // When we start playing another media, pause this one.
  //
  // Also mirror the `paused` state of the global audio element.
  useEffect(() => {
    const el = ref.current
    if (el == null) {
      log.error('media element not mounted, cannot pause it')
      return
    }
    const globalEl = mediaPlayerMutexCtx.audioElement

    if (
      mediaPlayerMutexCtx.currentSrc == null ||
      mediaPlayerMutexCtx.currentSrc !== props.src
    ) {
      return
    }

    // A bubble (re)mounting or becoming active while its message is already
    // mid-track on the singleton (profile/chat switch back, auto-advance with
    // a restored position) used to start displaying at 0:00 while the sound
    // kept playing (upstream #6378). Adopt the singleton's position when it
    // has real progress and the local element is fresh — the opposite carry
    // (restored local position → just-started singleton) lives in
    // onPlayGlobal below and requires the inverse state, so the two can't
    // fight.
    if (globalEl.currentTime > 1 && el.currentTime < 1) {
      el.currentTime = globalEl.currentTime
    }

    // This is very primitive syncing. Only the `paused` state,
    // no regard for `currentTime`, `playbackRate` etc.
    // But it's good enough for a start.
    const onPlayGlobal = () => {
      if (el.paused) {
        el.play()
        ignoreOnePlayEvent.current = true
      }
      // Asymmetric, one-way sync: if the singleton just started this src from
      // the top (e.g. auto-advance reached a message the user had half-heard)
      // while we hold a restored position, push our position onto it. Never the
      // reverse — a remounting local element must not yank back a mid-play
      // singleton.
      // eslint-disable-next-line react-hooks/immutability
      if (globalEl.currentTime < 1 && el.currentTime > 1) {
        globalEl.currentTime = el.currentTime
      }
    }
    const onPauseGlobal = () => {
      if (!el.paused) {
        el.pause()
        ignoreOnePauseEvent.current = true
      }
    }
    // A seek made on the singleton (mini-player slider, media-session
    // seek) must be copied onto this bubble's local element, or its display
    // desyncs and the two positionStore writers for this src fight (the stale
    // bubble value winning on singleton-originated pause). The delta guard
    // prevents ping-pong with the local→global onSeeking mirror: a local scrub
    // lands here with delta ≈ 0 and is skipped; the A2 asymmetric restore-push
    // likewise settles at delta ≈ 0.
    const onSeekedGlobal = () => {
      if (Math.abs(globalEl.currentTime - el.currentTime) > 0.5) {
        el.currentTime = globalEl.currentTime
      }
    }
    if (!globalEl.paused) {
      onPlayGlobal()
    }
    globalEl.addEventListener('play', onPlayGlobal)
    globalEl.addEventListener('pause', onPauseGlobal)
    globalEl.addEventListener('seeked', onSeekedGlobal)

    return () => {
      // The queued 'pause' event fires only AFTER this cleanup zeroes
      // currentTime below, so the position-store onPause would see 0 and skip
      // the takeover save — capture the real position here first.
      // Near-end counts as ended: on a natural end the local muted element can
      // lag the singleton's `ended` by tens of ms — saving duration-ε here
      // would resurrect the entry the singleton just cleared and park the
      // bubble at the end, making the next play end (and auto-advance)
      // instantly. (The `duration - 1` mirror of restorePosition's guard,
      // which the >1s fast-path on replay skips.)
      const nearEnd =
        Number.isFinite(el.duration) && el.currentTime >= el.duration - 1
      if (
        customControlsEnabledRef.current &&
        !el.ended &&
        !nearEnd &&
        el.currentTime > 1
      ) {
        positionStore.setPosition(props.src, el.currentTime)
      }
      el.pause()
      // Native mode resets the deactivated element like upstream always did.
      // Custom mode keeps the position: the bubble displaying 0:00 while a
      // replay would resume mid-way read as a glitch — the held position IS
      // what will happen on the next play.
      if (!customControlsEnabledRef.current || el.ended || nearEnd) {
        el.currentTime = 0
      }

      globalEl.removeEventListener('play', onPlayGlobal)
      globalEl.removeEventListener('pause', onPauseGlobal)
      globalEl.removeEventListener('seeked', onSeekedGlobal)
    }
  }, [
    mediaPlayerMutexCtx.audioElement,
    mediaPlayerMutexCtx.currentSrc,
    props.src,
  ])

  // Muted because it's gonna be the <audio> element inside of
  // `MediaPlayerMutexContext` that actually plays the media.
  const wrappedOnPlay: React.ReactEventHandler<HTMLMediaElement> = e => {
    props.onPlay?.(e)
    if (!ignoreOnePlayEvent.current) {
      onPlayNonProgrammatic?.(e)
      mediaPlayerMutexCtx.eventListeners.onPlay(e)
    }
    ignoreOnePlayEvent.current = false
  }
  const wrappedOnPause: React.ReactEventHandler<HTMLMediaElement> = e => {
    props.onPause?.(e)
    if (!ignoreOnePauseEvent.current) {
      mediaPlayerMutexCtx.eventListeners.onPause(e)
    }
    ignoreOnePauseEvent.current = false
  }

  // One stable render tree in both settings states: the <audio> element
  // identity must survive the settings toggle, otherwise flipping it
  // mid-playback remounts the local element at 0 while the singleton keeps
  // playing, desyncing the controls. OFF mode is byte-identical to before:
  // `controls={true}` renders like the hardcoded `controls`,
  // `className={undefined}` is a no-op through classNames(), `preload`
  // is omitted.
  return (
    <>
      <ForceMutedAudioPlayer
        ref={ref}
        preload={customControlsEnabled ? 'metadata' : undefined}
        {...props}
        {...mediaPlayerMutexCtx.eventListeners}
        controls={!customControlsEnabled}
        className={customControlsEnabled ? styles.hiddenAudio : undefined}
        onPlay={wrappedOnPlay}
        onPause={wrappedOnPause}
      />
      {customControlsEnabled && (
        <CustomAudioControls
          audioRef={ref}
          src={props.src}
          tabIndex={props.tabIndex}
        />
      )}
    </>
  )
}

/**
 * One dumb subscription mirroring an `<audio>` element's state into React.
 *
 * `holdTimeRef.current === true` suspends `currentTime` updates (the seek thumb
 * is being dragged), which is why `setCurrentTime` comes back out.
 */
export function useAudioElementState(
  audioRef: React.RefObject<HTMLAudioElement | null>,
  holdTimeRef?: React.RefObject<boolean>
) {
  const [paused, setPaused] = useState(true)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)

  useEffect(() => {
    const el = audioRef.current
    if (el == null) {
      return
    }
    const sync = () => {
      setPaused(el.paused)
      if (holdTimeRef?.current !== true) {
        setCurrentTime(el.currentTime)
      }
      setDuration(el.duration)
    }
    sync()
    const events = ['timeupdate', 'durationchange', 'play', 'pause'] as const
    events.forEach(ev => el.addEventListener(ev, sync))
    return () => events.forEach(ev => el.removeEventListener(ev, sync))
  }, [audioRef, holdTimeRef])

  const safeDuration = Number.isFinite(duration) ? duration : 0
  return {
    paused,
    currentTime,
    safeDuration,
    progress:
      safeDuration > 0 ? Math.min(currentTime, safeDuration) / safeDuration : 0,
    setCurrentTime,
  }
}

/**
 * Visible custom controls that drive the `<audio>` element in `audioRef`.
 *
 * In a message bubble that's the LOCAL muted element, and the existing
 * mirroring (see `MediaPlayerMutexContext`) turns those actions into actual
 * sound on the global singleton. The global mini-player passes the singleton
 * itself, so the same controls drive playback directly.
 */
export function CustomAudioControls({
  audioRef,
  src,
  tabIndex,
  compact,
}: {
  audioRef: React.RefObject<HTMLAudioElement | null>
  src: string
  tabIndex?: number
  /** Drop the 300px min-width so the controls fit a narrow column (mini-player). */
  compact?: boolean
}) {
  const tx = useTranslationFunction()
  const ctx = useContext(MediaPlayerMutexContext)
  // While the user drags the seek thumb, don't let `timeupdate` reassert the
  // controlled value out from under the pointer.
  const seekingRef = useRef(false)
  const { paused, currentTime, safeDuration, progress, setCurrentTime } =
    useAudioElementState(audioRef, seekingRef)
  // Waveform peaks (progressive enhancement). Tagged with the src they were
  // computed for so a src change instantly stops drawing the previous message's
  // waveform (peaks below is null until the new ones arrive). null = loading OR
  // fell back to the plain bar — either way the bar renders exactly as A1.
  const [peaksState, setPeaksState] = useState<{
    src: string
    peaks: Float32Array
  } | null>(null)
  const peaks = peaksState?.src === src ? peaksState.peaks : null

  // Keep the local rate in sync with the global one so all bubbles agree
  // (on mount and whenever the global rate changes). Setting it fires
  // `ratechange`, which the mirror applies to the singleton only when this
  // is the active src.
  useEffect(() => {
    const el = audioRef.current
    if (el != null) {
      el.playbackRate = ctx.playbackRate
      // Loading a new `src` resets `playbackRate` to `defaultPlaybackRate`,
      // so set both — same as setPlaybackRate does for the singleton.
      el.defaultPlaybackRate = ctx.playbackRate
    }
  }, [audioRef, ctx.playbackRate])

  // Peaks pipeline: kick off once per src; never blocks playback.
  useEffect(() => {
    let cancelled = false
    getPeaks(src).then(p => {
      if (!cancelled && p != null) {
        setPeaksState({ src, peaks: p })
      }
    })
    return () => {
      cancelled = true
    }
  }, [src])

  // Remembered position (A2): restore on metadata (and on replay from the top,
  // via the play button), save while playing (throttled ~2s) and immediately on
  // pause. Clear-on-ended is on the singleton (survives unmount).
  useEffect(() => {
    const el = audioRef.current
    if (el == null) {
      return
    }
    let lastSave = 0
    const restore = () => {
      // One-way only (same invariant as onPlayGlobal): never yank back a
      // mid-play singleton — a restore-seek here would be mirrored onto it by
      // the mutex onSeeking. If the singleton already owns this src, its own
      // position is the truth.
      const globalEl = ctx.audioElement
      if (
        !globalEl.paused &&
        globalEl.getAttribute('src') === src &&
        // Fresh start (<1s, e.g. auto-advance onto a half-heard message) may
        // still be restored — same fresh-start test as the onPlayGlobal carry.
        globalEl.currentTime >= 1
      ) {
        return
      }
      restorePosition(el, src)
    }
    const onTime = () => {
      if (el.paused || el.currentTime <= 1) {
        return
      }
      const t = Date.now()
      if (t - lastSave > 2000) {
        lastSave = t
        positionStore.setPosition(src, el.currentTime)
      }
    }
    const onPause = () => {
      if (!el.ended && el.currentTime > 1) {
        positionStore.setPosition(src, el.currentTime)
      }
    }
    // Clear-on-ended lives on the singleton (MediaPlayerMutexProvider) so it
    // fires even when this bubble unmounted mid-play; no local 'ended' needed.
    if (el.readyState >= 1 /* HAVE_METADATA */) {
      restore()
    }
    el.addEventListener('loadedmetadata', restore)
    el.addEventListener('timeupdate', onTime)
    el.addEventListener('pause', onPause)
    return () => {
      el.removeEventListener('loadedmetadata', restore)
      el.removeEventListener('timeupdate', onTime)
      el.removeEventListener('pause', onPause)
    }
  }, [audioRef, src, ctx.audioElement])

  return (
    <div
      className={classNames(
        styles.customControls,
        compact && styles.customControlsCompact
      )}
    >
      <IconButton
        icon={paused ? 'play' : 'pause'}
        coloring='currentColor'
        aria-label={paused ? tx('menu_play') : tx('menu_pause')}
        tabIndex={tabIndex}
        onClick={() => {
          const el = audioRef.current
          if (el == null) {
            return
          }
          // A user click is the non-programmatic path: the mirror's
          // ignore-refs are only set when the global element drives us.
          if (el.paused) {
            // Re-play within the same mount: a prior mutex takeover zeroed this
            // element (cleanup), so re-apply the saved position BEFORE play —
            // the scrub-carryover in mutex onPlay then brings the singleton
            // along. (No-op when resuming a same-src pause: currentTime >= 1.)
            if (el.currentTime < 1) {
              restorePosition(el, src)
            }
            el.play()
          } else {
            el.pause()
          }
        }}
      />
      {/* Two rows right of the play button: the seek/waveform gets the full
          width, time and speed sit below it (the usual messenger layout). */}
      <div className={styles.stack}>
        <div className={styles.seekWrap}>
          {peaks != null && (
            <WaveformCanvas
              peaks={peaks}
              progress={progress}
              className={styles.waveformCanvas}
            />
          )}
          <input
            type='range'
            className={classNames(
              styles.seek,
              peaks != null && styles.seekOverWaveform
            )}
            min={0}
            max={safeDuration}
            step='any'
            value={Math.min(currentTime, safeDuration)}
            tabIndex={tabIndex}
            aria-label={tx('a11y_audio_seek')}
            aria-valuetext={`${formatAudioTime(
              Math.min(currentTime, safeDuration)
            )} / ${formatAudioTime(safeDuration)}`}
            onPointerDown={() => {
              seekingRef.current = true
            }}
            onPointerUp={() => {
              seekingRef.current = false
            }}
            onChange={e => {
              const v = Number(e.currentTarget.value)
              // Update state too: with `sync()` suppressed during the drag, a
              // skip-only fix would leave the controlled value stale and snap
              // the thumb back on every pointermove.
              setCurrentTime(v)
              const el = audioRef.current
              if (el != null) {
                // A scrub before play is carried onto the singleton on play
                // (see MediaPlayerMutexContext.onPlay), so it's mirrored now.
                el.currentTime = v
              }
            }}
          />
        </div>
        <div className={styles.metaRow}>
          <span className={styles.time}>
            {formatAudioTime(currentTime)} / {formatAudioTime(safeDuration)}
          </span>
          <button
            type='button'
            className={styles.speedPill}
            tabIndex={tabIndex}
            aria-label={tx('a11y_playback_speed', `${ctx.playbackRate}×`)}
            onClick={() =>
              ctx.setPlaybackRate(nextPlaybackRate(ctx.playbackRate))
            }
          >
            {ctx.playbackRate}×
          </button>
        </div>
      </div>
    </div>
  )
}

export function AudioPlayerNonMutex({
  ref,
  className,
  ...restProps
}: Props & {
  ref?: React.RefObject<HTMLAudioElement | null>
}) {
  return (
    <audio
      ref={ref}
      controls
      className={classNames(styles.audioPlayer, className)}
      {...restProps}
    />
  )
}
